
import {
    PersonTypeId,
    PersonTypeOperation,
    PersonTypeCategory,
} from '@/constants/personTypeAccess';

export default class User
{
    static Type     = new class {
        AGENT       = 'AGENT'
        DEPARTMENT  = 'DEPARTMENT'
    }

    static PersonTypeId = PersonTypeId
    static PersonTypeOperation = PersonTypeOperation
    static PersonTypeCategory = PersonTypeCategory

    constructor(
        id,
        userType,
        active,
        name,
        username,
        roleNames,
        permissionNames,
        categoryNames,
        personTypeNames,
        lastLogin,
        personTypeOperations = {},
        categoryPersonTypeOperations = {},
        governorateIds = {},
        districtIds = {},
        townshipIds = {},
        regionIds = {},
    ){
        this.id                 = id ?? 0
        this.userType           = userType
        this.active             = active
        this.name               = name
        this.username           = username
        this.roleNames          = Array.isArray(roleNames) ? roleNames : []
        this.permissionNames    = Array.isArray(permissionNames) ? permissionNames : []
        this.categoryNames      = Array.isArray(categoryNames) ? categoryNames : []
        this.personTypeNames    = Array.isArray(personTypeNames) ? personTypeNames : []
        this.lastLogin          = lastLogin
        this.personTypeOperations = personTypeOperations && typeof personTypeOperations === 'object' ? personTypeOperations : {}
        this.categoryPersonTypeOperations = categoryPersonTypeOperations && typeof categoryPersonTypeOperations === 'object' ? categoryPersonTypeOperations : {}
        this.governorateIds          = Array.isArray(governorateIds) ? governorateIds : []
        this.districtIds          = Array.isArray(districtIds) ? districtIds : []
        this.townshipIds          = Array.isArray(townshipIds) ? townshipIds : []
        this.regionIds          = Array.isArray(regionIds) ? regionIds : []

    }
    //#region Getters houing functions
    setGovernorateIds(districts){
     if (!Array.isArray(districts)) {
            this.governorateIds = []
            return
        }
        this.governorateIds = [...new Set(
            districts
                .filter((r) => this.districtIds.includes(r.value))
                .map((r) => r.child?.value)
                .filter((value) => value !== undefined && value !== null)
        )]   
    }
    setDistrictIds(townships){
        if (!Array.isArray(townships)) {
            this.districtIds = []
            return
        }
        this.districtIds = [...new Set(
            townships
                .filter((r) => this.townshipIds.includes(r.value))
                .map((r) => r.child?.value)
                .filter((value) => value !== undefined && value !== null)
        )]
    }
    setTownshipIds(regions){
        if (!Array.isArray(regions)) {
            this.townshipIds = []
            return
        }
        this.townshipIds = [...new Set(
            regions
                .filter((r) => this.regionIds.includes(r.value))
                .map((r) => r.child?.value)
                .filter((value) => value !== undefined && value !== null)
        )]
    }
    //#endregion

    //#region User Type Functions
    is(type=null){
        return this.userType === type.trim().toUpperCase()
    }

    isAgent(){
        return this.is(User.Type.AGENT)
    }

    isDepartment(){
        return this.is(User.Type.DEPARTMENT)
    }
    //#endregion

    //#region Categories Functions
    hasCategory(catName = null){
        return this.categoryNames.includes(catName)
    }

    hasAnyCategories(catNames){
        let target = false
        for (let i = 0; i < catNames.length; i++){
            target = this.categoryNames.includes(catNames[i])
            if(target) return target
        }
        return target
    }

    hasAllCategories(catNames){
        let target = false
        for (let i = 0; i < catNames.length; i++){
            target = this.categoryNames.includes(catNames[i])
            if(!target) return target
        }
        return target
    }
    //#endregion

    //#region Person Types Functions
    hasPersonType(perTypeName = null){
        return this.personTypeNames.includes(perTypeName)
    }

    hasAnyPersonTypes(perTypeNames){
        let target = false
        for (let i = 0; i < perTypeNames.length; i++){
            target = this.personTypeNames.includes(perTypeNames[i])
            if(target) return target
        }
        return target
    }

    hasAllPersonTypes(perTypeNames){
        let target = false
        for (let i = 0; i < perTypeNames.length; i++){
            target = this.personTypeNames.includes(perTypeNames[i])
            if(!target) return target
        }
        return target
    }
    //#endregion

    //#region Person Type Operations Functions
    hasPersonTypeId(personTypeId = null){
        if(personTypeId == null) return false
        const normalizedId = String(personTypeId).trim()
        return this.personTypeNames.map((id) => String(id).trim()).includes(normalizedId)
    }

    hasPersonTypeOperation(personTypeId = null, operation = null){
        if(personTypeId == null || operation == null) return false

        const normalizedId = String(personTypeId)
        const normalizedOperation = String(operation).trim().toUpperCase()
        const operations = this.personTypeOperations[normalizedId]

        if(!Array.isArray(operations)) return false
        return operations.map((op) => String(op).trim().toUpperCase()).includes(normalizedOperation)
    }

    hasCategoryPersonTypeOperation(category = null, personTypeId = null, operation = null){
        if(category == null || personTypeId == null || operation == null) return false

        const normalizedCategory = String(category)
        const normalizedId = String(personTypeId)
        const normalizedOperation = String(operation).trim().toUpperCase()
        const categoryMap = this.categoryPersonTypeOperations?.[normalizedCategory]

        if(!categoryMap || typeof categoryMap !== 'object') return false

        const operations = categoryMap[normalizedId]
        if(!Array.isArray(operations)) return false

        return operations.map((op) => String(op).trim().toUpperCase()).includes(normalizedOperation)
    }
    //#endregion

    //#region Permissions Functions
    hasPermission(perName = null){
        return this.permissionNames.includes(perName)
    }

    hasAnyPermission(perNames){
        let target = false
        for (let i = 0; i < perNames.length; i++){
            target = this.permissionNames.includes(perNames[i])
            if(target) return target
        }
        return target
    }

    hasAllPermissions(perNames){
        let target = false
        for (let i = 0; i < perNames.length; i++){
            target = this.permissionNames.includes(perNames[i])
            if(!target) return target
        }
        return target
    }
    //#endregion

    //#region Roles Functions
    hasRole(rlName = null){
        return this.roleNames.includes(rlName)
    }

    hasAnyRole(rlNames){
        let target = false
        for (let i = 0; i < rlNames.length; i++){
            target = this.roleNames.includes(rlNames[i])
            if(target) return target
        }
        return target
    }

    hasAllRoles(rlNames){
        let target = false
        for (let i = 0; i < rlNames.length; i++){
            target = this.roleNames.includes(rlNames[i])
            if(!target) return target
        }
        return target
    }
    //#endregion
}